import { supabase } from '@/integrations/supabase/client';
import type { StatusOrdemServico } from '@/types/ordemServico';

/**
 * Status considerados "abertos" (não finalizados) — shared between the read + update paths
 * so both stay in lockstep. If the lifecycle expands, change this in one place.
 */
export const STATUS_OS_ABERTOS: StatusOrdemServico[] = ['pendente', 'aceita', 'em_execucao'];

export interface OrdemServicoAberta {
  id: string;
  numero_os: string | null;
  status: string | null;
  endereco: unknown; // JSONB — typed loosely to match Supabase generated type
}

export interface NovoEnderecoOS {
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  codigo_postal?: string | null;
  cidade?: string | null;
  regiao?: string | null;
  pais?: string | null;
}

/**
 * Lists OS in "open" statuses for a given cliente.
 * Org scoping via RLS on `ordens_servico`.
 */
export async function listOrdensServicoAbertas(clienteId: string): Promise<OrdemServicoAberta[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('id, numero_os, status, endereco')
    .eq('cliente_id', clienteId)
    .in('status', STATUS_OS_ABERTOS);

  if (error) throw error;
  return (data ?? []) as OrdemServicoAberta[];
}

/**
 * Updates the `endereco` JSONB on all OS in "open" statuses for a given cliente.
 * Returns the count of OS updated (0 if none were open).
 * Missing fields fall back to '' (the WeClear-era 'Portugal' fallback was dropped on 2026-05-28).
 * Uses `||` (not `??`) to match legacy behavior of treating null AND empty-string as "absent".
 */
export async function atualizarEnderecoOSAbertas(
  clienteId: string,
  novoEndereco: NovoEnderecoOS,
): Promise<number> {
  const { data: osAbertas, error: fetchError } = await supabase
    .from('ordens_servico')
    .select('id')
    .eq('cliente_id', clienteId)
    .in('status', STATUS_OS_ABERTOS);

  if (fetchError) throw fetchError;
  if (!osAbertas || osAbertas.length === 0) return 0;

  const enderecoFormatado = {
    rua: novoEndereco.rua || '',
    numero: novoEndereco.numero || '',
    complemento: novoEndereco.complemento || '',
    codigo_postal: novoEndereco.codigo_postal || '',
    cidade: novoEndereco.cidade || '',
    regiao: novoEndereco.regiao || '',
    pais: novoEndereco.pais || '',
  };

  const { error: updateError } = await supabase
    .from('ordens_servico')
    .update({ endereco: enderecoFormatado })
    .eq('cliente_id', clienteId)
    .in('status', STATUS_OS_ABERTOS);

  if (updateError) throw updateError;
  return osAbertas.length;
}
